import { Injectable } from '@angular/core';
import { PaletteModel } from '../models/palettes/palette_model';

/**
 * Registry service for dynamically resolving palette IDs by name.
 * This eliminates hardcoded palette IDs that may differ between environments.
 */
@Injectable({
  providedIn: 'root'
})
export class PaletteRegistryService {
  private builtInPalettes: Map<string, number> = new Map();
  private builtInPaletteIds: Set<number> = new Set();
  private initialized = false;

  /**
   * Initialize the registry with palettes loaded from the API.
   * Should be called after palettes are fetched from the backend.
   */
  initialize(palettes: PaletteModel[]): void {
    this.builtInPalettes.clear();
    this.builtInPaletteIds.clear();

    palettes
      .filter(p => p.builtIn)
      .forEach(p => {
        this.builtInPalettes.set(p.name, p.paletteId);
        this.builtInPaletteIds.add(p.paletteId);
      });

    this.initialized = true;
  }

  /**
   * Check if the registry has been initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get the Miyuki Delica palette ID
   */
  getMiyukiDelicaId(): number | null {
    return this.getIdByNamePattern('Miyuki Delica');
  }

  /**
   * Get the Preciosa Rocailles palette ID
   */
  getPreciosaRocaillesId(): number | null {
    return this.getIdByNamePattern('Preciosa');
  }

  /**
   * Check if a palette ID is a known built-in palette
   */
  isBuiltInPaletteId(paletteId: number): boolean {
    return this.builtInPaletteIds.has(paletteId);
  }

  /**
   * Get all built-in palette IDs
   */
  getBuiltInPaletteIds(): number[] {
    return Array.from(this.builtInPaletteIds);
  }

  /**
   * Get palette name by ID
   */
  getPaletteName(paletteId: number): string | null {
    for (const [name, id] of this.builtInPalettes) {
      if (id === paletteId) {
        return name;
      }
    }
    return null;
  }

  /**
   * Get the folder name for bead images based on palette ID
   */
  getBeadImageFolder(paletteId: number): string | null {
    const miyukiId = this.getMiyukiDelicaId();
    const preciosaId = this.getPreciosaRocaillesId();

    if (paletteId === miyukiId) {
      return 'miyuki-delica-11';
    }
    if (paletteId === preciosaId) {
      return 'preciosa-ornela-rocailles-11';
    }
    return null;
  }

  /**
   * Find a palette ID by matching a pattern in the palette name
   */
  private getIdByNamePattern(pattern: string): number | null {
    for (const [name, id] of this.builtInPalettes) {
      if (name.includes(pattern)) {
        return id;
      }
    }
    return null;
  }
}
